import { useState, useCallback } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { BillingCandidate, BillingInvoice } from '../types';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { handleEdgeFunctionError } from '../../../utils/edgeFunctionUtils';

// Helper to map API response to UI format
const mapBillingCandidate = (apiCandidate: any): BillingCandidate => {
  return {
    application_id: apiCandidate.application_id,
    candidate_id: apiCandidate.candidate_id || apiCandidate.candidateId,
    candidate_name: apiCandidate.candidate_name,
    job_title: apiCandidate.job_title,
    hired_date: apiCandidate.hired_date,
    hired_month: apiCandidate.hired_month,
    poc_id: apiCandidate.poc_id,
    poc_name: apiCandidate.poc_name,
    fee_amount: apiCandidate.fee_amount,
    outlet_id: apiCandidate.outlet_id,
    outlet_name: apiCandidate.outlet_name,
  };
};

const mapBillingInvoice = (apiInvoice: any): BillingInvoice => {
  return {
    invoiceNumber: apiInvoice.invoiceNumber || '',
    generatedDate: apiInvoice.generatedDate || null,
    dueDate: apiInvoice.dueDate || null,
    status: apiInvoice.status || '',
    outletName: apiInvoice.outletName || '',
    managerName: apiInvoice.managerName || '',
    // UI helpers
    id: apiInvoice.invoiceNumber || '',
    invoiceName: apiInvoice.invoiceNumber || '',
    totalAmount: 0, // API doesn't seem to return amount in the list?
  };
};

export const useBillingCandidates = () => {
  const [candidates, setCandidates] = useState<BillingCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-billing-candidates', {
        body: {},
      });

      if (error) {
        const extractedError = await handleEdgeFunctionError(error);
        throw extractedError;
      }
      if (data?.error) throw new Error(data.error.message || 'Failed to fetch candidates');

      const mappedData = (data?.data || []).map(mapBillingCandidate);
      setCandidates(mappedData);
    } catch (err: any) {
      console.error('Error fetching billing candidates:', err);
      showSnackbar('Failed to fetch billing history', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return { candidates, loading, fetchCandidates };
};

export const useBillingInvoices = () => {
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-billing-invoices', {
        body: {},
      });

      if (error) {
        const extractedError = await handleEdgeFunctionError(error);
        throw extractedError;
      }
      if (data?.error) throw new Error(data.error.message || 'Failed to fetch invoices');

      const mappedData = (data?.data || []).map(mapBillingInvoice);
      setInvoices(mappedData);
    } catch (err: any) {
      console.error('Error fetching billing invoices:', err);
      showSnackbar('Failed to fetch invoices', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return { invoices, loading, fetchInvoices };
};

export const useInvoicePdf = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const getInvoicePdf = useCallback(async (filename: string, mode: 'view' | 'download' = 'view') => {
    if (!filename) return;
    setLoading(true);
    try {
      const functionName = mode === 'download' ? 'download-invoice-pdf' : 'get-invoice-pdf';
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { invoice_filename: filename },
        responseType: 'arraybuffer',
      } as any);

      if (error) {
        const extractedError = await handleEdgeFunctionError(error);
        throw extractedError;
      }

      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (mode === 'download') {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(url, '_blank');
      }

      // Cleanup
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

    } catch (err: any) {
      console.error(`Error ${mode}ing invoice PDF:`, err);
      showSnackbar(`Failed to ${mode} invoice`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const fetchInvoicePdf = useCallback(async (filename: string): Promise<Blob | null> => {
    if (!filename) return null;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-invoice-pdf', {
        body: { invoice_filename: filename },
        responseType: 'arraybuffer',
      } as any);

      if (error) {
        const extractedError = await handleEdgeFunctionError(error);
        throw extractedError;
      }

      return new Blob([data], { type: 'application/pdf' });
    } catch (err: any) {
      console.error('Error fetching invoice PDF:', err);
      showSnackbar('Failed to load invoice preview', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return { getInvoicePdf, fetchInvoicePdf, loading };
};
